import { createContext, useContext } from 'react';
import { z } from 'zod';

/**
 * ZodContext interface
 * Defines the shape of the Zod context
 */
export interface ZodContextType {
  /**
   * The Zod schema
   */
  schema?: z.ZodType<any>;

  /**
   * Check if a field exists in the schema
   * @param field - The field name to check
   * @param formData - The current form data (optional)
   * @returns true if the field exists in the schema, false otherwise
   */
  hasField: (field: string, formData?: any) => boolean;
}

/**
 * Default context value
 */
const defaultContext: ZodContextType = {
  schema: undefined,
  hasField: () => true, // By default, assume all fields exist
};

/**
 * Zod context
 * This context is used to provide the Zod schema to form components
 */
export const ZodContext = createContext<ZodContextType>(defaultContext);

/**
 * Function to check if a field exists in the schema
 * @param schema - The Zod schema
 * @param field - The field name to check
 * @param formData - The current form data (optional)
 * @returns true if the field exists in the schema, false otherwise
 */
export const hasField = (
  schema: z.ZodType<any> | undefined,
  field: string,
  formData?: any,
): boolean => {
  if (!schema) {
    // eslint-disable-next-line no-console
    console.warn(
      `Zod schema is not provided to Form. Cannot check for field (${field}) existence.`,
    );
    return true; // If no schema, assume all fields exist
  }

  // Handle ZodEffects by extracting the inner schema
  if (schema instanceof z.ZodEffects) {
    return hasField(schema._def.schema, field, formData);
  }

  // Get the shape of the schema if it's an object schema
  const shape = (schema as any)._def?.shape;

  // If it's an object schema with a shape, check if the field exists in the shape
  if (shape) {
    return Object.keys(shape()).includes(field);
  }

  // If it's a discriminated union schema, check if the field exists in the active option
  const discriminator = (schema as any)._def?.discriminator;
  const options = (schema as any)._def?.options;

  if (discriminator && options && Array.isArray(options) && formData) {
    // Get the discriminator field value from the form data
    const discriminatorValue = formData[discriminator];

    if (discriminatorValue) {
      // Find the option that matches the discriminator value
      const activeOption = options.find(
        (option: any) =>
          option._def?.shape?.()[discriminator]?._def?.value ===
          discriminatorValue,
      );

      // If we found an active option, check if the field exists in it
      if (activeOption) {
        return hasField(activeOption, field);
      }
    }

    // If we couldn't determine the active option, fall back to checking all options
    return options.some(option => hasField(option, field));
  }
  if (options && Array.isArray(options)) {
    // If no form data is provided or it's not a discriminated union with a discriminator,
    // fall back to checking all options
    return options.some(option => hasField(option, field));
  }

  // If it's neither an object schema nor a discriminated union, we can't determine if the field exists
  // eslint-disable-next-line no-console
  console.warn(
    `Cannot determine if field (${field}) exists in the schema. Schema type is not supported.`,
  );
  return false; // Default to false to be safe
};

/**
 * Hook to use the Zod context
 * @param schema - Optional Zod schema to use instead of the one from context
 * @returns The Zod context with schema and hasField function
 */
export const useZodContext = (schema?: z.ZodType<any>) => {
  const context = useContext(ZodContext);

  // If a schema is provided, use it instead of the one from context
  const effectiveSchema = schema || context.schema;

  // Return a new context with the effective schema and a hasField function that uses it
  return {
    schema: effectiveSchema,
    hasField: (field: string, formData?: any) =>
      hasField(effectiveSchema, field, formData),
  };
};
